import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
  HiPlus,
  HiOutlineTrash,
  HiPencil,
  HiSave,
  HiX,
  HiOutlineUpload,
  HiOutlinePhotograph,
} from "react-icons/hi";
import { GiCartwheel } from "react-icons/gi";
import { FaTrophy } from "react-icons/fa";
import { ColorInput } from "../../../atoms/ColorInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { Switch } from "../../../atoms/Switch";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import useAuth from "../../../../hooks/useAuth";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../modals/PlansModal";
import { TextInput, Select } from "flowbite-react";
import { Spinner } from "../../../atoms/Spinner";
import { FieldLabel } from "../../../atoms/FieldLabel";
import {
  GAME_TYPE,
  SEGMENT_TYPE,
  PLAY_FREQUENCY,
  GamingSegment,
  GamingConfiguration,
} from "../../../../constants/types";
import { useUploadFile } from "../../../../hooks/cdn";
import { CharacterCounter } from "../../../atoms/CharacterCounter";

interface GamingSettingsProps {
  data?: {
    gaming?: GamingConfiguration;
  };
  onSubmit: (gamingConfig: GamingConfiguration) => Promise<void>;
  onChange?: (gamingConfig: GamingConfiguration) => void;
}

const DEFAULT_COLORS = [
  "#E63946",
  "#F4A261",
  "#2A9D8F",
  "#E9C46A",
  "#264653",
  "#8338EC",
  "#FF006E",
  "#3A86FF",
  "#06D6A0",
  "#FB5607",
  "#7209B7",
  "#00B4D8",
];

const GamingSettings: React.FC<GamingSettingsProps> = ({
  data,
  onSubmit,
  onChange,
}) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [editingSegmentId, setEditingSegmentId] = useState<string | null>(null);
  const [uploadingSegmentId, setUploadingSegmentId] = useState<string | null>(
    null
  );
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const { mutate: uploadFile } = useUploadFile();

  const defaultSegmentIdsRef = useRef<string[]>([
    uuidv4(),
    uuidv4(),
    uuidv4(),
    uuidv4(),
    uuidv4(),
    uuidv4(),
  ]);

  const defaultSegments = useMemo<GamingSegment[]>(
    () => [
      {
        id: defaultSegmentIdsRef.current[0],
        type: SEGMENT_TYPE.PRIZE,
        display_text: t("dashboard.features.lmc.gaming.jackpot"),
        color: "#F4A261",
        quantity_available: 5,
        quantity_used: 0,
        probability: 1,
        gift_description: t(
          "dashboard.features.lmc.gaming.jackpotDesc"
        ) as string,
      },
      {
        id: defaultSegmentIdsRef.current[1],
        type: SEGMENT_TYPE.PRIZE,
        display_text: t("dashboard.features.lmc.gaming.off", { off: 25 }),
        color: "#8338EC",
        quantity_available: 20,
        quantity_used: 0,
        probability: 4,
      },
      {
        id: defaultSegmentIdsRef.current[2],
        type: SEGMENT_TYPE.PRIZE,
        display_text: t("dashboard.features.lmc.gaming.off", { off: 15 }),
        color: "#3A86FF",
        quantity_available: null,
        quantity_used: 0,
        probability: 10,
      },
      {
        id: defaultSegmentIdsRef.current[3],
        type: SEGMENT_TYPE.PRIZE,
        display_text: t("dashboard.features.lmc.gaming.off", { off: 10 }),
        color: "#2A9D8F",
        quantity_available: null,
        quantity_used: 0,
        probability: 20,
      },
      {
        id: defaultSegmentIdsRef.current[4],
        type: SEGMENT_TYPE.PRIZE,
        display_text: t("dashboard.features.lmc.gaming.off", { off: 5 }),
        color: "#06D6A0",
        quantity_available: null,
        quantity_used: 0,
        probability: 25,
      },
      {
        id: defaultSegmentIdsRef.current[5],
        type: SEGMENT_TYPE.NO_WIN,
        display_text: t("dashboard.features.lmc.gaming.betterLuck"),
        color: "#E63946",
        probability: 40,
      },
    ],
    [t]
  );

  const [gamingConfig, setGamingConfig] = useState<GamingConfiguration>(() => {
    if (data?.gaming) {
      return data.gaming;
    }
    return {
      enabled: false,
      game_type: GAME_TYPE.SPINNER,
      version: 1,
      play_frequency: PLAY_FREQUENCY.DAILY,
      segments: [],
    };
  });

  useEffect(() => {
    if (data?.gaming) {
      setGamingConfig(data.gaming);
    }
  }, [data?.gaming]);

  useEffect(() => {
    if (!data?.gaming) {
      setGamingConfig((prev) => ({
        ...prev,
        segments: defaultSegments,
      }));
    }
  }, [defaultSegments, data?.gaming]);

  useEffect(() => {
    onChange?.(gamingConfig);
  }, [gamingConfig, onChange]);

  const hasFeatureAccess = permissions.hasFeatureAccess("LOYALTY_GAMING");
  const canWrite = permissions.can(Cap.LOYALTY, Level.WRITE);

  const handleToggleEnabled = useCallback(
    async (enabled: boolean) => {
      if (enabled && !hasFeatureAccess) {
        setShowUpgradeModal(true);
        return;
      }

      const newConfig = { ...gamingConfig, enabled };
      setGamingConfig(newConfig);

      try {
        setIsSubmitting(true);
        await onSubmit(newConfig);
        toast.success(
          enabled
            ? t("dashboard.features.lmc.gaming.enabled")
            : t("dashboard.features.lmc.gaming.disabled")
        );
      } catch (error) {
        setGamingConfig((prev) => ({ ...prev, enabled: !enabled }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [gamingConfig, hasFeatureAccess, onSubmit, t]
  );

  const handleGameTypeChange = (gameType: GAME_TYPE) => {
    setGamingConfig((prev) => ({ ...prev, game_type: gameType }));
  };

  const handleFrequencyChange = (frequency: PLAY_FREQUENCY) => {
    setGamingConfig((prev) => ({ ...prev, play_frequency: frequency }));
  };

  const handleAddSegment = () => {
    const maxSegments = gamingConfig.game_type === GAME_TYPE.SPINNER ? 12 : 9;

    if (gamingConfig.segments.length >= maxSegments) {
      toast.warning(
        t("dashboard.features.lmc.gaming.maxSegments", { count: maxSegments })
      );
      return;
    }

    const colorIndex = gamingConfig.segments.length % DEFAULT_COLORS.length;
    const newSegment: GamingSegment = {
      id: uuidv4(),
      type: SEGMENT_TYPE.PRIZE,
      display_text: t("dashboard.features.lmc.gaming.newPrize"),
      color: DEFAULT_COLORS[colorIndex],
      gift_description: "",
      quantity_available: null,
      quantity_used: 0,
      probability: 5,
    };

    setGamingConfig((prev) => {
      const newSegments = [...prev.segments, newSegment];
      return {
        ...prev,
        segments: recalculateProbabilities(newSegments, newSegment.id),
      };
    });
    setEditingSegmentId(newSegment.id);
  };

  const handleRemoveSegment = (segmentId: string) => {
    const minSegments = gamingConfig.game_type === GAME_TYPE.SPINNER ? 4 : 1;

    if (gamingConfig.segments.length <= minSegments) {
      toast.warning(t("dashboard.features.lmc.gaming.minSegments"));
      return;
    }

    const segment = gamingConfig.segments.find((s) => s.id === segmentId);
    if (segment?.type === SEGMENT_TYPE.NO_WIN) {
      const noWinCount = gamingConfig.segments.filter(
        (s) => s.type === SEGMENT_TYPE.NO_WIN
      ).length;
      if (noWinCount <= 1) {
        toast.warning(t("dashboard.features.lmc.gaming.needNoWin"));
        return;
      }
    }

    setGamingConfig((prev) => {
      const filteredSegments = prev.segments.filter((s) => s.id !== segmentId);
      return {
        ...prev,
        segments: recalculateProbabilities(filteredSegments, ""),
      };
    });
  };

  const handleUpdateSegment = (
    segmentId: string,
    updates: Partial<GamingSegment>
  ) => {
    if (updates.probability !== undefined) {
      const remainderSegment = findRemainderSegment(gamingConfig.segments);

      const sumOfOtherSegments = gamingConfig.segments
        .filter((s) => s.id !== segmentId && s.id !== remainderSegment?.id)
        .reduce((sum, s) => sum + (s.probability ?? 0), 0);

      const newTotal = sumOfOtherSegments + updates.probability;

      if (newTotal > 100) {
        toast.error(t("dashboard.features.lmc.gaming.probabilityExceeds100"));
        return;
      }
    }

    setGamingConfig((prev) => {
      const updatedSegments = prev.segments.map((s) =>
        s.id === segmentId ? { ...s, ...updates } : s
      );

      if (updates.probability !== undefined) {
        return {
          ...prev,
          segments: recalculateProbabilities(updatedSegments, segmentId),
        };
      }

      return {
        ...prev,
        segments: updatedSegments,
      };
    });
  };

  const findRemainderSegment = (
    segments: GamingSegment[]
  ): GamingSegment | undefined => {
    const betterLuckSegment = segments.find(
      (s) =>
        s.type === SEGMENT_TYPE.NO_WIN &&
        s.display_text.toLowerCase().includes("better luck")
    );
    if (betterLuckSegment) return betterLuckSegment;

    return segments.find((s) => s.type === SEGMENT_TYPE.NO_WIN);
  };

  const recalculateProbabilities = (
    segments: GamingSegment[],
    changedSegmentId: string
  ): GamingSegment[] => {
    const remainderSegment = findRemainderSegment(segments);
    if (!remainderSegment) return segments;

    if (remainderSegment.id === changedSegmentId) return segments;

    const sumWithoutRemainder = segments
      .filter((s) => s.id !== remainderSegment.id)
      .reduce((sum, s) => sum + (s.probability ?? 0), 0);

    const remainderProbability = Math.max(0, 100 - sumWithoutRemainder);

    return segments.map((s) =>
      s.id === remainderSegment.id
        ? { ...s, probability: remainderProbability }
        : s
    );
  };

  const handleImageUpload = (segmentId: string, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file.type.startsWith("image/")) {
      toast.error(t("dashboard.features.lmc.gaming.imageOnly"));
      return;
    }

    if (file.size > 5 * 1048576) {
      toast.error(t("dashboard.features.lmc.gaming.imageSize"));
      return;
    }

    setUploadingSegmentId(segmentId);

    uploadFile(
      {
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        data: { file } as any,
      },
      {
        onSuccess: (res: any) => {
          const cdnPath = res?.data?.element[0]?.cdnPath;
          if (cdnPath) {
            handleUpdateSegment(segmentId, { image_url: cdnPath });
            toast.success(t("dashboard.features.lmc.gaming.imageUploaded"));
          }
          setUploadingSegmentId(null);
          if (fileInputRefs.current[segmentId]) {
            fileInputRefs.current[segmentId]!.value = "";
          }
        },
        onError: () => setUploadingSegmentId(null),
      }
    );
  };

  const handleRemoveImage = (segmentId: string) => {
    handleUpdateSegment(segmentId, { image_url: undefined });
    toast.success(t("dashboard.features.lmc.gaming.imageRemoved"));
  };

  const handleSaveConfiguration = async () => {
    const hasNoWin = gamingConfig.segments.some(
      (s) => s.type === SEGMENT_TYPE.NO_WIN
    );
    if (!hasNoWin) {
      toast.error(t("dashboard.features.lmc.gaming.needNoWin"));
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(gamingConfig);
      toast.success(t("dashboard.features.lmc.gaming.saved"));
      setEditingSegmentId(null);
    } catch (error) {
      toast.error(t("dashboard.features.lmc.gaming.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSegmentEditor = (segment: GamingSegment) => {
    const isEditing = editingSegmentId === segment.id;

    if (!isEditing) {
      return (
        <div
          key={segment.id}
          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
        >
          {segment.image_url ? (
            <img
              src={segment.image_url}
              alt={segment.display_text}
              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0"
            />
          ) : (
            <div
              className="w-8 h-8 rounded-full border-2 border-white shadow-sm flex-shrink-0"
              style={{ backgroundColor: segment.color }}
            />
          )}
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-900 truncate block">
              {segment.display_text}
            </span>
            <div className="text-xs text-gray-500">
              {segment.type === SEGMENT_TYPE.NO_WIN
                ? t("dashboard.features.lmc.gaming.probabilityToLose")
                : t("dashboard.features.lmc.gaming.probability")}
              : {segment.probability}%
            </div>
          </div>
          {segment.type === SEGMENT_TYPE.PRIZE &&
            (segment.quantity_used ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full flex-shrink-0">
                <FaTrophy className="w-3 h-3" color="orange" />{" "}
                {segment.quantity_used}
              </span>
            )}
          {canWrite && (
            <div className="flex items-center gap-2">
              <CustomButton
                variant="none"
                size="icon"
                onClick={() => setEditingSegmentId(segment.id)}
                className="rounded-md text-gray-400 transition-colors enabled:hover:text-blue-600"
                icon={<HiPencil size={18} />}
                aria-label={t("common.edit") as string}
              />
              {segment.type !== SEGMENT_TYPE.NO_WIN && (
                <CustomButton
                  variant="none"
                  size="icon"
                  onClick={() => handleRemoveSegment(segment.id)}
                  className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                  icon={<HiOutlineTrash size={18} />}
                  aria-label={t("common.delete") as string}
                />
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={segment.id}
        className="p-4 bg-white rounded-lg border border-blue-500 space-y-4"
      >
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-900">
            {t("dashboard.features.lmc.gaming.editSegment")}
          </span>
          <CustomButton
            variant="none"
            size="icon"
            onClick={() => setEditingSegmentId(null)}
            className="rounded-md text-gray-500 enabled:hover:text-gray-700"
            icon={<HiX size={18} />}
            aria-label={t("common.close") as string}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <FieldLabel htmlFor={`display-${segment.id}`}>
              {t("dashboard.features.lmc.gaming.displayText")}
            </FieldLabel>
            <TextInput
              id={`display-${segment.id}`}
              value={segment.display_text}
              maxLength={25}
              onChange={(e) =>
                handleUpdateSegment(segment.id, {
                  display_text: e.target.value,
                })
              }
              placeholder="e.g., 10% OFF"
            />
            <CharacterCounter
              currentLength={segment.display_text?.length || 0}
              maxLength={25}
              className="mt-0"
            />
          </div>
          <div>
            <FieldLabel htmlFor={`color-${segment.id}`}>
              {t("dashboard.features.lmc.gaming.color")}
            </FieldLabel>
            <ColorInput
              id={`color-${segment.id}`}
              value={segment.color}
              onChange={(color) => handleUpdateSegment(segment.id, { color })}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <FieldLabel htmlFor={`prob-${segment.id}`}>
              {segment.type === SEGMENT_TYPE.NO_WIN
                ? t("dashboard.features.lmc.gaming.probabilityToLose")
                : t("dashboard.features.lmc.gaming.probability")}
            </FieldLabel>
            <div className="flex items-center gap-2">
              <TextInput
                id={`prob-${segment.id}`}
                type="number"
                min="0"
                max="100"
                step="0.1"
                disabled={segment.type === SEGMENT_TYPE.NO_WIN}
                value={segment.probability ?? 0}
                onChange={(e) =>
                  handleUpdateSegment(segment.id, {
                    probability: Math.min(
                      100,
                      Math.max(0, parseFloat(e.target.value) || 0)
                    ),
                  })
                }
                className="w-full"
              />
              <span className="text-gray-500 font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {segment.type === SEGMENT_TYPE.NO_WIN
                ? t("dashboard.features.lmc.gaming.probabilityToLoseHelp")
                : t("dashboard.features.lmc.gaming.probabilityHelp")}
            </p>
          </div>
        </div>

        {segment.type === SEGMENT_TYPE.PRIZE && (
          <>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <FieldLabel htmlFor={`qty-${segment.id}`}>
                  {t("dashboard.features.lmc.gaming.quantity")}
                </FieldLabel>
                <TextInput
                  id={`qty-${segment.id}`}
                  type="number"
                  min="0"
                  value={segment.quantity_available ?? ""}
                  onChange={(e) =>
                    handleUpdateSegment(segment.id, {
                      quantity_available: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  placeholder={
                    t("dashboard.features.lmc.gaming.unlimited") as string
                  }
                />
              </div>
            </div>

            <div>
              <FieldLabel htmlFor={`desc-${segment.id}`}>
                {t("dashboard.features.lmc.gaming.description")}
              </FieldLabel>
              <TextInput
                id={`desc-${segment.id}`}
                value={segment.gift_description ?? ""}
                onChange={(e) =>
                  handleUpdateSegment(segment.id, {
                    gift_description: e.target.value,
                  })
                }
                maxLength={50}
                placeholder={
                  t("dashboard.features.lmc.gaming.descPlaceholder") as string
                }
              />
              <CharacterCounter
                currentLength={segment.gift_description?.length || 0}
                maxLength={50}
                className="mt-0"
              />
            </div>
          </>
        )}

        <div>
          <FieldLabel>{t("dashboard.features.lmc.gaming.image")}</FieldLabel>
          {segment.image_url ? (
            <div className="space-y-2">
              <div className="relative inline-block">
                <img
                  src={segment.image_url}
                  alt={segment.display_text}
                  className="w-32 h-32 object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  onClick={() => handleRemoveImage(segment.id)}
                  className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                  type="button"
                >
                  <HiX size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <HiOutlinePhotograph
                size={32}
                className="text-gray-400 mx-auto mb-2"
              />
              <p className="text-sm text-gray-600 mb-2">
                {t("dashboard.features.lmc.gaming.uploadImage")}
              </p>
              <CustomButton
                type="button"
                variant="primary"
                className="inline-flex"
                onClick={() => fileInputRefs.current[segment.id]?.click()}
                disabled={uploadingSegmentId === segment.id}
                icon={
                  uploadingSegmentId === segment.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <HiOutlineUpload size={16} />
                  )
                }
                title={
                  uploadingSegmentId === segment.id
                    ? t("dashboard.features.lmc.gaming.uploading")
                    : t("dashboard.features.lmc.gaming.uploadButton")
                }
              />
              <p className="text-xs text-gray-500 mt-2">
                {t("dashboard.features.lmc.gaming.imageFormats")}
              </p>
            </div>
          )}
          <input
            type="file"
            hidden
            ref={(el) => (fileInputRefs.current[segment.id] = el)}
            accept="image/*"
            onChange={(e) => handleImageUpload(segment.id, e.target.files)}
          />
        </div>
      </div>
    );
  };

  if (!hasFeatureAccess) {
    return (
      <div className="p-4 border border-gray-200 rounded-lg bg-white">
        <div className="flex justify-center mb-6">
          <img
            src="https://cdn.1cdn.app/application/UMD/25121710283980_sum_gaming.png"
            alt={t("callbot.imageAlt") as string}
            className="w-[200px] rounded-xl"
          />
        </div>
        <div className="flex items-center mb-4 gap-2">
          <GiCartwheel className="text-gray-400 flex-shrink-0" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.gaming.title")}
          </h3>
        </div>
        <FeatureUpgradePrompt
          featureName={"Loyalty Gaming"}
          featureDescription={
            t("dashboard.features.lmc.gaming.upgradeDesc") as string
          }
          benefits={[
            t("dashboard.features.lmc.gaming.upgradeBenefit1") as string,
            t("dashboard.features.lmc.gaming.upgradeBenefit2") as string,
            t("dashboard.features.lmc.gaming.upgradeBenefit3") as string,
            t("dashboard.features.lmc.gaming.upgradeBenefit4") as string,
          ]}
          onUpgrade={() => setShowUpgradeModal(true)}
          className="mb-5"
        />
        <PlansModal
          showPlansModal={showUpgradeModal}
          setShowPlansModal={setShowUpgradeModal}
        />
      </div>
    );
  }

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white space-y-6">
      <div className="flex justify-center mb-6">
        <img
          src="https://cdn.1cdn.app/application/UMD/25121710283980_sum_gaming.png"
          alt={t("callbot.imageAlt") as string}
          className="w-[200px] rounded-xl"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GiCartwheel className="text-gray-400 flex-shrink-0" size={18} />
          <h3 className="text-base font-semibold text-gray-900">
            {t("dashboard.features.lmc.gaming.title")}
          </h3>
        </div>
        <Switch
          checked={gamingConfig.enabled}
          disabled={isSubmitting || !canWrite}
          onChange={handleToggleEnabled}
        />
      </div>

      {gamingConfig.enabled && (
        <>
          <div>
            <FieldLabel className="mb-2 block">
              {t("dashboard.features.lmc.gaming.gameType")}
            </FieldLabel>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleGameTypeChange(GAME_TYPE.SPINNER)}
                disabled={!canWrite}
                className={`p-4 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  gamingConfig.game_type === GAME_TYPE.SPINNER
                    ? "border-blue-500"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <img
                  src="https://cdn.1cdn.app/application/UMD/25121715543279_spin.png"
                  alt="Spinner Wheel"
                  className="w-12 h-12 mx-auto mb-2 object-contain"
                />
                <div className="font-medium text-center">
                  {t("dashboard.features.lmc.gaming.spinnerWheel")}
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleGameTypeChange(GAME_TYPE.SCRATCH_CARD)}
                disabled={!canWrite}
                className={`p-4 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  gamingConfig.game_type === GAME_TYPE.SCRATCH_CARD
                    ? "border-blue-500"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <img
                  src="https://cdn.1cdn.app/application/UMD/2512171554330_scratch.png"
                  alt="Scratch Card"
                  className="w-12 h-12 mx-auto mb-2 object-contain"
                />
                <div className="font-medium text-center">
                  {t("dashboard.features.lmc.gaming.scratchCard")}
                </div>
              </button>
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="play-frequency" className="mb-2 block">
              {t("dashboard.features.lmc.gaming.playFrequency")}
            </FieldLabel>
            <Select
              id="play-frequency"
              value={gamingConfig.play_frequency}
              disabled={!canWrite}
              onChange={(e) =>
                handleFrequencyChange(e.target.value as PLAY_FREQUENCY)
              }
            >
              <option value={PLAY_FREQUENCY.ONCE}>
                {t("dashboard.features.lmc.gaming.once")}
              </option>
              <option value={PLAY_FREQUENCY.HOURLY}>
                {t("dashboard.features.lmc.gaming.hourly")}
              </option>
              <option value={PLAY_FREQUENCY.DAILY}>
                {t("dashboard.features.lmc.gaming.daily")}
              </option>
              <option value={PLAY_FREQUENCY.WEEKLY}>
                {t("dashboard.features.lmc.gaming.weekly")}
              </option>
              <option value={PLAY_FREQUENCY.MONTHLY}>
                {t("dashboard.features.lmc.gaming.monthly")}
              </option>
              <option value={PLAY_FREQUENCY.YEARLY}>
                {t("dashboard.features.lmc.gaming.yearly")}
              </option>
            </Select>
          </div>

          <div>
            <FieldLabel htmlFor="game-description">
              {t("dashboard.features.lmc.gaming.gameDescription")}
            </FieldLabel>
            <p className="mb-1 text-xs text-gray-500">
              {t("dashboard.features.lmc.gaming.gameDescriptionHelp")}
            </p>
            <TextInput
              id="game-description"
              value={gamingConfig.description ?? ""}
              disabled={!canWrite}
              onChange={(e) =>
                setGamingConfig((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={
                t(
                  "dashboard.features.lmc.gaming.gameDescriptionPlaceholder"
                ) as string
              }
              maxLength={60}
            />
            <CharacterCounter
              currentLength={gamingConfig.description?.length || 0}
              maxLength={60}
              className="mt-0"
            />
          </div>

          <div translate="no">
            <div className="flex items-center justify-between mb-3">
              <FieldLabel className="mb-0">
                {t("dashboard.features.lmc.gaming.prize")} (
                {gamingConfig.segments.length})
              </FieldLabel>
              {canWrite &&
                gamingConfig.segments.length <
                  (gamingConfig.game_type === GAME_TYPE.SPINNER ? 12 : 9) && (
                  <CustomButton
                    variant="none"
                    size="sm"
                    onClick={handleAddSegment}
                    className="gap-1 px-0 text-blue-600 enabled:hover:text-blue-700"
                    icon={<HiPlus size={16} />}
                    title={t("dashboard.features.lmc.gaming.addSegment")}
                  />
                )}
            </div>
            <div className="space-y-2">
              {gamingConfig.segments.map((segment) =>
                renderSegmentEditor(segment)
              )}
            </div>
          </div>

          <div className="flex justify-end" translate="no">
            <CustomButton
              onClick={handleSaveConfiguration}
              disabled={isSubmitting || !canWrite}
              title={
                isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Spinner size="sm" />
                    {t("dashboard.features.lmc.gaming.saving")}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <HiSave size={18} />
                    {t("dashboard.features.lmc.gaming.saveConfig")}
                  </span>
                )
              }
              variant="primary"
            />
          </div>
        </>
      )}

      <PlansModal
        showPlansModal={showUpgradeModal}
        setShowPlansModal={setShowUpgradeModal}
      />
    </div>
  );
};

export default GamingSettings;
